import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/** Manual price override dialog — shared by Deals and Set Detail. */
export function ManualPriceDialog({ state, setState, onDataChanged }) {
  const { open, field, setNumber, currentValue, isManual, currentNote } = state;
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setValue(currentValue != null ? String(currentValue) : "");
    setNote(currentNote || "");
  }, [open, currentValue, currentNote]);

  const labels = {
    bl_sealed: { title: "BL Lowest Sealed", hint: "Use when scraper counts open-box listings as sealed." },
    bl_avg: { title: "BL Avg Sealed", hint: "Use when BL avg is skewed by outliers or open-box." },
    amz_buy_box: { title: "Amazon Buy Box", hint: "Use when Keepa lags the current listing price you see live." },
    pc_sealed: { title: "PriceCharting Sealed", hint: "Pin the true sealed price from PriceCharting after you eyeball it." },
  };
  const cfg = labels[field] || { title: field, hint: "" };
  const close = () => setState({ ...state, open: false });

  const save = async () => {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) { toast.error("Enter a positive number"); return; }
    setSaving(true);
    try {
      await api.setManualPrice(setNumber, field, num, note || undefined);
      toast.success(`Pinned $${num.toFixed(2)} · ${cfg.title}`);
      close();
      onDataChanged && onDataChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const clearManual = async () => {
    setSaving(true);
    try {
      await api.clearManualPrice(setNumber, field);
      toast.success(`Cleared manual ${cfg.title} — will use scan value`);
      close();
      onDataChanged && onDataChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E4E0] rounded-sm text-gray-900 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-head uppercase tracking-tighter">
            Manual {cfg.title} · <span className="text-[#E3000B]">{setNumber}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] font-mono text-gray-500 leading-relaxed">
            {cfg.hint} Overrides the scanned value everywhere: the table, the offer generator, and downstream ROI.
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              Verified {cfg.title} ($)
            </Label>
            <Input
              type="number" step="0.01" min="0" value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={currentValue != null ? String(currentValue) : "e.g. 49.99"}
              className="h-9 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-sm"
              data-testid="manual-price-input" autoFocus />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. verified on site 3/2"
              className="h-9 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs"
              data-testid="manual-price-note" />
          </div>
          {isManual && (
            <div className="text-[10px] font-mono text-[#E3000B]/80 border border-[#E3000B]/30 bg-[#E3000B]/[0.05] rounded-sm p-2">
              Currently pinned{currentNote ? `: "${currentNote}"` : ""}. Change or clear below.
            </div>
          )}
        </div>
        <DialogFooter>
          {isManual && (
            <Button onClick={clearManual} disabled={saving} variant="outline"
              className="h-9 rounded-sm border-[#FF3333] bg-transparent text-[#FF3333] font-bold uppercase tracking-wider text-xs hover:bg-[#FF3333]/10"
              data-testid="manual-price-clear">
              <X size={12} className="mr-1" /> CLEAR OVERRIDE
            </Button>
          )}
          <Button onClick={save} disabled={saving}
            className="h-9 rounded-sm bg-[#E3000B] text-white font-bold uppercase tracking-wider hover:bg-[#E6B800] disabled:opacity-50"
            data-testid="manual-price-save">
            {saving ? "SAVING…" : "SAVE"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
